import { catchError, map, takeUntil } from 'rxjs/operators';
import { Component, Inject, OnInit, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { BuyerAccountService } from '~core/modules/buyer-account';
import {
  ACCESS_DENIED_PAGE,
  ACCOUNT_INFORMATION_LINK,
  BUY_AGAIN,
  BUY_AGAIN_EXTERNAL,
  ORDER_HISTORY_LINK,
  ORDER_HISTORY_LINK_EXTERNAL,
  ORDER_HISTORY_LINK_PERSONAL,
  ORDER_HISTORY_LINK_PERSONAL_EXTERNAL,
  STORED_PAYMENT_METHOD_LINK,
  YOUR_ASSORTMENT,
  YOUR_ASSORTMENT_EXTERNAL,
} from '~shared/constants/links.constants';
import { MediaService } from '~shared/services/media.service';
import { BaseComponentClassDirective } from '~shared/classes/base-component.class';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { ACCOUNT_MENU_ITEM_LIST } from '../../constants/account.constant';
import { AccountService } from '../../services/account.service';
import { Observable, of } from 'rxjs';
import { FeatureToggleEnum } from '~shared/constants';
import { Router } from '@angular/router';
import { AccountInfoContext } from '~shared/constants/account-info.constant';
import { environment } from '~env/environment';
import { AuthorizationIdamStateService } from '~app/modules/authorization/services/authorization-idam-state.service';
import { AuthorizationStateType } from '~app/modules/authorization/constants/authorization.constant';
import { AccountType, IAccountInformation } from '~shared/models';
import { isPlatformBrowser } from '@angular/common';
import { USER_TYPE_KEY } from '~app/core/modules/identity/constants/identity.constant';
import { EmployeeService } from '~app/modules/authorization/services/employee.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';

const ACCOUNT_PAGE_ROUTE_DEPTH = 3;

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AccountComponent extends BaseComponentClassDirective implements OnInit {
  authState$ = this.authIdamStateService.state$;
  public templateOptions = {
    isChildPage: false,
    isAccountInfoPage: false,
    isOrderHistoryPage: false,
    isPersonalOrderHistoryPage: false,
    menuItemList: ACCOUNT_MENU_ITEM_LIST,
    parentPage: null,
    isMobileView$: null,
    accountInfo$: null,
    isEntrepreneur$: null,
    isBuyAgainPage: false,
    isYourAssortmentPage: false,
    isAccessDeniedPage: false,
    personalContext: AccountInfoContext.personal,
    absolutePageUrl: null,
    isPageTitleDisplayed: false,
    isTabsVisible: false,
  };
  accountType$ = this.accountInfoService.accountType$;
  loggedInState = AuthorizationStateType.LoggedIn;
  EMPLOYEE_AS_CUSTOMER = AccountType.EMPLOYEE_AS_CUSTOMER;
  EMPLOYEE = AccountType.EMPLOYEE;
  isEmployee = false;
  pageTitleHiddenListing = [
    YOUR_ASSORTMENT,
    ACCESS_DENIED_PAGE,
    BUY_AGAIN,
    ORDER_HISTORY_LINK,
    ORDER_HISTORY_LINK_PERSONAL,
    STORED_PAYMENT_METHOD_LINK,
  ];
  tabsHiddingList = [ACCESS_DENIED_PAGE, STORED_PAYMENT_METHOD_LINK];
  customerInfo: Observable<IAccountInformation>;
  featureFlagShoppingList: Observable<boolean> = this.featureFlagService.isFeatureEnabledAndReadyV2(
    FeatureToggleEnum.FF_BOE_ECK_SHOPPING_LIST
  );
  private isUsingExternalOrdersHistoryLink = false;

  featureFlagPaymentMethod: Observable<boolean>;
  isStoredPaymentMethodPageDisabled: boolean;
  public handleStartWithNewCustomer = this.employeeService.handleStopImpersonatingCustomer;
  public onEmployeeLogout = () => this.employeeService.handleEmployeeLogout().subscribe();

  constructor(
    private accountService: AccountService,
    private mediaService: MediaService,
    private buyerAccountService: BuyerAccountService,
    private accountInfoService: AccountInfoService,
    private router: Router,
    private authIdamStateService: AuthorizationIdamStateService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private employeeService: EmployeeService,
    private readonly featureFlagService: ActiveFeaturesConfigcatService
  ) {
    super();

    if (isPlatformBrowser(this.platformId)) {
      this.isEmployee = localStorage.getItem(USER_TYPE_KEY) === 'EMP';

      this.customerInfo = this.accountInfoService.getAccountInfo();
    }
  }

  public ngOnInit(): void {
    this.templateOptions.isMobileView$ = this.mediaService.mediaAliases$.pipe(
      map(() => this.mediaService.isAliasActive(MEDIA_ALISES.mobile))
    );
    this.checkFeatureFlagPaymentMethodPage();
    this.handleRouteChange();
    this.accountService.routeChange$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.handleRouteChange());

    this.templateOptions.accountInfo$ = this.accountInfoService.getAccountInfo();
    this.templateOptions.isEntrepreneur$ = this.accountInfoService.isEntrepreneur();

    this.featureFlagService
      .isFeatureEnabledAndReadyV2(FeatureToggleEnum.FF_TMP_ODR_1741_USE_EXTERNAL_LINK_TO_OHP_FROM_MY_ORDERS_TAB)
      .subscribe((value) => {
        this.isUsingExternalOrdersHistoryLink = value;
      });
  }

  public onSupportClick(): void {
    this.buyerAccountService.loginToSSO();
  }

  private checkFeatureFlagPaymentMethodPage(): void {
    this.featureFlagPaymentMethod = this.featureFlagService
      .isFeatureEnabledAndReadyV2(FeatureToggleEnum.FF_BOE_STORE_PAYMENT_METHODS)
      .pipe(
        map((isPaymentLinkEnabled) => {
          if (isPaymentLinkEnabled) {
            return isPaymentLinkEnabled;
          } else {
            this.isStoredPaymentMethodPageDisabled = true;
            return false;
          }
        }),
        catchError(() => {
          this.isStoredPaymentMethodPageDisabled = true;
          return of(false);
        })
      );
  }

  public navigateToOrderHistoryPage(context: string): void {
    if (this.isUsingExternalOrdersHistoryLink) {
      if (this.templateOptions.isPersonalOrderHistoryPage || context === AccountInfoContext.personal) {
        window.location.href = ORDER_HISTORY_LINK_PERSONAL_EXTERNAL();
      } else {
        window.location.href = ORDER_HISTORY_LINK_EXTERNAL();
      }
    } else {
      this.router.navigateByUrl(
        this.templateOptions.isPersonalOrderHistoryPage || context === AccountInfoContext.personal
          ? ORDER_HISTORY_LINK_PERSONAL
          : ORDER_HISTORY_LINK
      );
    }
  }

  public navigateToBuyAgainPage(event: Event): void {
    event.preventDefault();
    window.location.href = BUY_AGAIN_EXTERNAL();
  }

  public navigateToShoppingListPage(event: Event): void {
    event.preventDefault();
    window.location.href = YOUR_ASSORTMENT_EXTERNAL();
  }

  private handleRouteChange(): void {
    const currentPagePath = this.accountService.currentPage.path;
    const currentPagePathWithParamters = this.accountService.pagePathWithFirstQueryParameter;
    this.templateOptions.absolutePageUrl = environment.appBaseUrl() + currentPagePathWithParamters;
    this.templateOptions.parentPage = this.accountService.parentPage;
    this.templateOptions.isChildPage = currentPagePath.split('/').length > ACCOUNT_PAGE_ROUTE_DEPTH;
    this.templateOptions.isAccountInfoPage = currentPagePath === ACCOUNT_INFORMATION_LINK;
    this.templateOptions.isOrderHistoryPage = currentPagePath === ORDER_HISTORY_LINK;
    this.templateOptions.isPersonalOrderHistoryPage = currentPagePathWithParamters === ORDER_HISTORY_LINK_PERSONAL;
    this.templateOptions.isBuyAgainPage = currentPagePath === BUY_AGAIN;
    this.templateOptions.isYourAssortmentPage = currentPagePath === YOUR_ASSORTMENT;
    this.templateOptions.isAccessDeniedPage = currentPagePath === ACCESS_DENIED_PAGE;
    this.templateOptions.isPageTitleDisplayed = this.getPageTitleVisibility(currentPagePath);
    this.templateOptions.isTabsVisible = this.getTabsVisibility(currentPagePath);
  }

  private getPageTitleVisibility(currentPath: string): boolean {
    return !this.pageTitleHiddenListing.includes(currentPath);
  }

  private getTabsVisibility(currentPath: string): boolean {
    return !this.tabsHiddingList.includes(currentPath);
  }
}
